import { useMemo, useState } from 'react';
import { genres, movies } from '@/mocks/movies';

type SortKey = 'douban' | 'heat' | 'year';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'douban', label: '按评分' },
  { key: 'heat', label: '按热度' },
  { key: 'year', label: '按年份' },
];

export default function MovieExplorer() {
  const [activeGenre, setActiveGenre] = useState('全部');
  const [sortKey, setSortKey] = useState<SortKey>('douban');
  const [keyword, setKeyword] = useState('');

  const results = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return movies
      .filter((m) => (activeGenre === '全部' ? true : m.genres.includes(activeGenre)))
      .filter((m) =>
        kw
          ? m.title.toLowerCase().includes(kw) ||
            m.region.toLowerCase().includes(kw) ||
            m.genres.join('').includes(kw)
          : true,
      )
      .sort((a, b) => {
        if (sortKey === 'year') return b.year - a.year;
        return b[sortKey] - a[sortKey];
      });
  }, [activeGenre, sortKey, keyword]);

  return (
    <section id="explore" className="w-full bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <div className="w-full max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <span className="text-xs md:text-sm font-medium text-primary-600 tracking-widest">EXPLORE</span>
              <h2 className="mt-2 font-heading font-bold text-2xl md:text-4xl text-foreground-950">
                按类型选片：从 18 部高口碑作品开始
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
                选择你偏好的类型，或直接搜索片名、地区。所有筛选都只作用于本页已收录的影片，无需跳转，点一下就能看到结果。
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="relative">
                <i
                  className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"
                  aria-hidden="true"
                ></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索片名 / 地区 / 类型"
                  aria-label="搜索影片"
                  className="w-full sm:w-64 pl-9 pr-3 py-2.5 rounded-md border border-background-300/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                />
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-wrap gap-2" role="group" aria-label="类型筛选">
              {genres.map((g) => {
                const active = g === activeGenre;
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setActiveGenre(g)}
                    aria-pressed={active}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      active
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700'
                    }`}
                  >
                    {g}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-1 p-1 rounded-full bg-secondary-100 w-max">
              {sortOptions.map((opt) => {
                const active = opt.key === sortKey;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSortKey(opt.key)}
                    aria-pressed={active}
                    className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      active ? 'bg-secondary-500 text-background-50' : 'text-secondary-900 hover:bg-secondary-200/70'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            共找到 <strong className="text-foreground-950 font-semibold">{results.length}</strong> 部影片
            {activeGenre !== '全部' && <span> · 类型：{activeGenre}</span>}
          </p>

          {results.length === 0 ? (
            <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400" aria-hidden="true"></i>
              <p className="mt-3 text-foreground-700">没有匹配的影片，换个关键词或类型试试吧。</p>
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {results.map((m) => (
                <article
                  key={m.id}
                  className="group rounded-lg border border-background-200/80 bg-background-50 p-4 md:p-5 hover:border-primary-300 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-heading font-bold text-base md:text-lg text-foreground-950 truncate">
                        {m.title}
                      </h3>
                      <p className="mt-1 text-xs md:text-sm text-foreground-500">
                        {m.year} · {m.region}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="font-heading font-bold text-lg md:text-xl text-accent-600">
                        {m.douban.toFixed(1)}
                      </span>
                      <p className="text-[11px] text-foreground-400">整理评分</p>
                    </div>
                  </div>

                  <p className="mt-3 text-xs md:text-sm text-foreground-600 leading-relaxed">{m.oneLiner}</p>

                  <div className="mt-4 flex flex-wrap items-center gap-1.5">
                    {m.genres.map((g) => (
                      <span
                        key={g}
                        className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-900 text-[11px] md:text-xs"
                      >
                        {g}
                      </span>
                    ))}
                    <span className="px-2 py-0.5 rounded-md bg-primary-100 text-primary-800 text-[11px] md:text-xs">
                      热度 {m.heat}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}